import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Route, withRouter } from 'react-router';
import mergeWith from 'lodash/mergeWith';
import pick from 'lodash/pick';
import throttle from 'lodash/throttle';

import { AppContext } from '@prisjakt/ui-app-context';
import RouteRenderer from './route-renderer';
import LoadIndicator from './loader';
import RouteContext from './context';

import MetaTags from '../components/meta-tags';
import errorPageMetaTags from '../components/meta-tags/error-page';
import ErrorBoundary from '../components/error-boundary';
import Loader from '../components/loader';
import ScrollToTop from '../components/scroll-to-top';

import matchRoute from './match-route';
import ErrorPage from './error-page';

const { START, PRIMARY_REQUEST_DONE, DONE, TIMEOUT } = {
  START: 0.2,
  PRIMARY_REQUEST_DONE: 0.7,
  DONE: 1,
  TIMEOUT: Infinity,
};

const FETCH_MAX_RETRIES = 3;
const FETCH_RETRY_SLEEP = 3000;
const sleep = async t => new Promise(resolve => setTimeout(resolve, t));

const nextIdleTick = (cb, opts) => {
  // This should never happen, but if it does. trigger cb directly
  if (IS_SERVER) {
    cb();
    return;
  }

  // Use requestIdleCallback for supported browsers
  if ('requestIdleCallback' in window) {
    window.requestIdleCallback(cb, opts);
    return;
  }

  // Fallback to requestAnimationFrame (which is polyfilled i client/index)
  window.requestAnimationFrame(cb);
};

class Routes extends React.PureComponent {
  routeContextValue = {
    dispatch: action => this.dispatchRouteAction(action),
    getState: () =>
      this.state.route.key && this.routeState[this.state.route.key]
        ? this.routeState[this.state.route.key]
        : {},
  };

  constructor(props) {
    super(props);

    const { campaigns, routeConfig, fetchContext } = props;

    this.activeCampaign =
      campaigns && campaigns.active.find(c => c.type === 'OFFER');

    this.state = {
      data: undefined,
      errors: undefined,
      showDelayedLoader: false,
      timeout: false,
      showSpinner: true,
      ...this.props.fetchContext.prefetch,
      route: this.props.fetchContext.route,
      fetchState: DONE,
      criticalError: undefined,
      routingError: undefined,
      loadOrders: {},
      loadedSections: {},
    };

    this.cache = {};
    this.routeState = {};
    this.dataCacheAge = {};

    if (IS_BROWSER) {
      window.__dataRoute = this;
    }

    if (this.state.route) {
      const { key } = this.state.route;

      Object.assign(this.state.route, routeConfig[this.state.route.name]);

      if (fetchContext.prefetch) {
        this.routeState[key] = fetchContext.route.state;
        this.cache[key] = fetchContext.prefetch;
        this.dataCacheAge[key] = Date.now();
        this.state.loadOrders[key] = 0;
        this.state.loadedSections[Routes.getRouteGroupKey(this.state.route)] =
          fetchContext.loadedSections || {};
      } else {
        this.state.loadedSections[
          Routes.getRouteGroupKey(this.state.route)
        ] = {};
        this.state.loadOrders[key] = -1;
      }

      // Never ever change the initial route id. This needs to be syncronised in
      // GTM and server side
      this.state.routeId = 'initial';
    }
  }

  updateDatalayer(initial = false) {
    if (IS_SERVER) {
      return;
    }

    if (!window.dataLayer) {
      if (this.context.captureException) {
        this.context.captureException(
          new Error('window.dataLayer is not defined')
        );
      }
      return;
    }

    const { data, route } = this.state;

    const routeData = {
      page: route && route.name,
      routeId: this.state.routeId,
      section:
        (route &&
          route.match &&
          route.match.params &&
          route.match.params.section) ||
        undefined,
    };

    // Since Google Tag Manager does a recursive merge of data in its data layer
    // we do a reset of our route data here to avoid merges of arrays and objects
    // when navigating between routes with the same route name
    if (initial && data) {
      const dataReset = {};

      Object.keys(data).forEach(key => {
        dataReset[key] = undefined;
      });

      window.dataLayer.push({
        event: 'resetRouteDataLayer',
        ...routeData,
        ...dataReset,
      });
    }

    window.dataLayer.push({
      event: initial ? 'routeLoaded' : 'routeLoadedMoreData',
      ...routeData,
      ...data,
    });
  }

  failTransition(error, timeout) {
    const { captureException } = this.context;

    captureException(error, {
      extra: this.state,
    });

    this.setState({
      routingError: error,
      timeout: Boolean(timeout),
      fetchState: TIMEOUT,
    });
  }

  static getRouteGroupKey(route) {
    if (!route) {
      return false;
    }

    if (route.component && typeof route.component.group === 'function') {
      return route.component.group(route);
    }
    const paramsString =
      route.match && route.match.params
        ? Object.keys(route.match.params)
            .map(key => route.match.params[key])
            .join('_')
        : 'no-params';
    return `${route.name}_${paramsString}`;
  }

  mergeResponseData(data, newData, mergeStrategy) {
    const { component } = this.state.route;
    if (component && typeof component.dataReducer === 'function') {
      return newData;
    }
    if (component && typeof component.mergeData === 'function') {
      return component.mergeData(data, newData);
    }
    return mergeWith({}, data, newData, mergeStrategy);
  }

  throttledGetData = throttle(async (...args) => {
    await this.getData(...args);
    this.dispatchRouteAction(
      {
        type: 'ROUTE_DATA_REFETCHED',
        location: this.props.location,
        route: this.state.route,
        data: this.state.data,
      },
      { fetch: 'none' }
    );
  }, 1000);

  async queryWithRetry(
    routeName,
    routeId,
    maxRetries,
    graphQL,
    variables,
    key
  ) {
    let response;
    let retries;
    for (retries = 0; retries <= maxRetries && !response; retries += 1) {
      try {
        // eslint-disable-next-line no-await-in-loop
        response = await this.props.fetchContext.query(
          graphQL,
          variables,
          key,
          routeName
        );
      } catch (err) {
        // Only retry on TypeError (network error from fetch API)
        if (err.name !== 'TypeError' || retries > maxRetries || !IS_BROWSER) {
          break;
        }
        // eslint-disable-next-line no-await-in-loop
        await sleep(FETCH_RETRY_SLEEP);
        // Abort retry if new transition was started
        if (
          this.state.nextRouteId !== routeId &&
          !(
            this.state.nextRouteId === undefined &&
            routeId === this.state.routeId
          )
        ) {
          return undefined;
        }
      }
    }
    if (!response && retries > maxRetries) {
      throw new Error(
        `TransitionRouter, failed to fetch data after ${retries - 1} retries.`
      );
    }
    return response;
  }

  async getData(
    route,
    routeId,
    fetchMoreVariables,
    didRenderFromCache = false
  ) {
    const isFetchingMore = !!fetchMoreVariables;
    const { key } = route;
    const { graphQL } = route.component;
    const routeState = this.routeState[key] || {};

    const groupKey = Routes.getRouteGroupKey(route);

    const isSameRouteGroup =
      groupKey === Routes.getRouteGroupKey(this.state.route);

    const loadOrder = Routes.getLoadOrder(
      route.component,
      route.variables,
      isSameRouteGroup ? this.state.loadedSections[groupKey] : undefined,
      routeId === 'initial'
    );

    const { loadOrders } = this.state;

    const loadOrderIncrement = isFetchingMore ? 0 : 1;
    let loadOrderIndex =
      (loadOrders[key] !== undefined ? loadOrders[key] : -1) +
      loadOrderIncrement;

    if (didRenderFromCache) {
      loadOrderIndex = loadOrder.length - 1;
    }

    const loadOrderVariables = didRenderFromCache
      ? loadOrder.reduce((all, vars) => Object.assign(all, vars), {})
      : loadOrder[loadOrderIndex];

    const stateVariables = route.component.stateToVariables
      ? route.component.stateToVariables(routeState)
      : {};

    const variables = {
      ...route.variables,
      ...stateVariables,
      ...(fetchMoreVariables || loadOrderVariables),
      campaignId: this.activeCampaign && this.activeCampaign.id,
      personalizationClientId: this.props.personalizationClientId,
      pulseEnvironmentId: this.props.pulseEnvironmentId,
    };

    try {
      const response =
        (await this.queryWithRetry(
          route.name,
          routeId,
          FETCH_MAX_RETRIES,
          graphQL,
          variables,
          key
        )) || {};

      this.setState({
        loadOrders: {
          ...loadOrders,
          [key]: loadOrderIndex,
        },
        loadedSections: {
          ...this.state.loadedSections,
          [groupKey]: {
            ...this.state.loadedSections[groupKey],
            ...loadOrderVariables,
          },
        },
      });

      if (isSameRouteGroup) {
        response.data = this.mergeResponseData(
          this.state.data || {},
          response ? response.data : {}
        );
      }

      if (isFetchingMore) {
        response.data = this.mergeResponseData(
          this.cache[key] ? this.cache[key].data : {},
          response ? response.data : {},
          (objValue, srcValue) => {
            if (Array.isArray(objValue)) {
              return objValue.concat(srcValue);
            }
            return undefined;
          }
        );
      } else if (loadOrderIndex) {
        response.data = this.mergeResponseData(
          this.cache[key] ? this.cache[key].data : {},
          response ? response.data : {}
        );

        // Set state after each load order received if route hasn't changed
        if (routeId === this.state.routeId) {
          this.setState(
            {
              ...response,
              fetchState:
                loadOrderIndex >= loadOrder.length - 1
                  ? DONE
                  : PRIMARY_REQUEST_DONE,
            },
            () => {
              this.updateDatalayer();
            }
          );
        }
      }

      if (response.data) {
        this.cache[key] = { data: response.data };
        this.dataCacheAge[key] = Date.now();
      }

      if (!isFetchingMore && loadOrder[loadOrderIndex + 1]) {
        nextIdleTick(
          () => {
            this.getData(route, routeId);
          },
          { timeout: 5000 }
        );
      }

      return response;
    } catch (e) {
      if (loadOrderIndex === 0 && !isFetchingMore) {
        this.failTransition(e, true);
      } else {
        this.failTransition(e, false);
      }
      return undefined;
    }
  }

  componentDidMount() {
    // If the URL has a hash, client side routing has to re-run on mount
    // since the server doesn't know about the hash part
    if (this.props.location.hash) {
      const { routeConfig, location, routes } = this.props;
      const nextRoute = matchRoute({
        useClassicUrls: this.context.market.useClassicUrls,
        useHashForProductTabs: this.context.market.useHashForProductTabs,
        routeConfig,
        localizedRoutes: routes,
        location,
        marketCode: this.context.market.code,
        experiments: this.props.fetchContext.experiments,
      });
      if (
        Boolean(nextRoute) !== Boolean(this.state.route) ||
        (nextRoute &&
          this.state.route &&
          this.state.route.key !== nextRoute.key)
      ) {
        this.run();
        return;
      }
    }

    this.updateDatalayer(true);

    const dispatchRouteLoaded = props => {
      const action = {
        type: 'ROUTE_LOADED',
        location: this.props.location,
        route: this.state.route,
        ...props,
      };

      this.props.dispatch(action);

      if (
        this.state.route &&
        this.state.route.component &&
        this.state.route.component.reducer
      ) {
        this.dispatchRouteAction(action, { fetch: 'none' });
      }
    };

    const hasInitialData = !!this.state.data;
    const needsData = this.state.route && this.state.route.component.graphQL;

    if (!needsData || hasInitialData) {
      dispatchRouteLoaded(pick(this.state, 'data', 'errors'));
    }

    if (!needsData) {
      return;
    }

    nextIdleTick(
      async () => {
        const routeData = await this.getData(
          this.state.route,
          this.state.routeId
        );
        // If we didn't have any data before, we need to trigger the route loaded action now
        if (!hasInitialData && routeData) {
          const { data, errors } = routeData;
          dispatchRouteLoaded({ data, errors });
        }
      },
      { timeout: 5000 }
    );
  }

  setTimers() {
    this.resetTimers();

    if (this.props.loaderAfter > 1) {
      this.deferredLoaderTimeout = setTimeout(() => {
        this.setState({
          showDelayedLoader: true,
        });
      }, this.props.loaderAfter);
    }

    if (this.props.timeoutAfter > 0) {
      this.timeoutAfterTimeout = setTimeout(() => {
        this.failTransition(
          new Error(
            `TransitionRouter, Max timeout of ${this.props.timeoutAfter} milliseconds reached`
          ),
          true
        );
      }, this.props.timeoutAfter);
    }
  }

  resetTimers() {
    clearTimeout(this.deferredLoaderTimeout);
    clearTimeout(this.timeoutAfterTimeout);
  }

  componentDidUpdate(prevProps) {
    // The refreshRoute property on the location state lets us push to history without triggering a
    // full route reload, as a performance optimisation. However, we don't want to use it when e.g.
    // using the browser Back button (the POP action), as that leads to bugs.
    if (
      this.props.history.location?.state?.refreshRoute === false &&
      this.props.history.action !== 'POP'
    ) {
      return;
    }

    // Listen to url updates
    if (this.props.location.key === prevProps.location.key) {
      return;
    }

    this.setTimers();
    this.run();
  }

  isSameRoute(nextRoute) {
    const { route } = this.state;

    return (
      route &&
      nextRoute &&
      route.variables &&
      nextRoute.variables &&
      route.name === nextRoute.name &&
      (route.variables.id === nextRoute.variables.id ||
        route.variables.slug === nextRoute.variables.slug)
    );
  }

  startTransition(newState, nextRoute) {
    const isSameRoute = this.isSameRoute(nextRoute);

    this.setState(
      {
        showDelayedLoader: false,
        fetchState: START,
        showSpinner: !isSameRoute,
        ...newState,
      },
      () => {
        this.props.onTransitionStart(this.props.location, this.state);
      }
    );
  }

  makeTransition(newState) {
    if (
      newState.route &&
      newState.route.component &&
      newState.route.component.reducer
    ) {
      // Dispatch route action before so reducer
      // has time to build state before navigation
      this.dispatchRouteAction(
        {
          type: 'ROUTE_LOADED',
          location: this.props.location,
          route: newState.route,
          data: newState.data,
          errors: newState.errors,
        },
        { fetch: 'none', route: newState.route }
      );
    }

    this.setState(
      {
        nextRouteId: undefined,
        showDelayedLoader: false,
        dataCacheAge: undefined,
        timeout: false,
        ...newState,
      },
      () => {
        this.props.onTransitionEnd(this.props.location, this.state);
        this.updateDatalayer(true);

        this.props.dispatch({
          type: 'ROUTE_LOADED',
          location: this.props.location,
          route: this.state.route,
          data: this.state.data,
          errors: this.state.errors,
        });
      }
    );
  }

  static getLoadOrder(
    component,
    variables,
    existingData = {},
    initialRoute = false
  ) {
    if (typeof component.loadOrder === 'function') {
      const loadOrders = component.loadOrder(variables, existingData, {
        initialRoute,
      });

      // Remove all { value: false } as they should not exists in
      // final request. Also if load order block becomes empty. ditch it
      const filteredLoadOrders = loadOrders
        .map(loaders =>
          Object.keys(loaders).reduce((all, key) => {
            if (loaders[key] !== true) {
              return all;
            }

            return Object.assign(all, { [key]: true });
          }, {})
        )
        .filter(x => Object.keys(x).length);

      return filteredLoadOrders;
    }

    return component.loadOrder || [];
  }

  async dispatchRouteAction(
    action,
    { fetch = 'throttled', route = this.state.route, dataReducer = false } = {}
  ) {
    const { key, component } = route;

    if (
      dataReducer &&
      component &&
      typeof component.dataReducer === 'function'
    ) {
      const { data } = this.cache[key];
      const newData = component.dataReducer(action, data);

      if (newData === data) {
        return;
      }

      this.cache[key] = { data: newData };
      this.dataCacheAge[key] = Date.now();

      this.setState({
        data: newData,
      });
      return;
    }

    if (component && typeof component.reducer === 'function') {
      const state = this.routeState[key];

      const newState = component.reducer(state, action);

      if (typeof newState === 'function') {
        newState({
          dispatch: this.dispatchRouteAction.bind(this),
          graphql: this.props.fetchContext.query,
          history: this.props.history,
        });
        return;
      }

      if (state !== newState) {
        this.routeState[key] = newState;
        this.forceUpdate();

        if (fetch === 'throttled') {
          this.throttledGetData(this.state.route, this.state.routeId);
        }
      }
    }
  }

  async run() {
    const { routeConfig, location, routes } = this.props;
    const nextRoute = matchRoute({
      useClassicUrls: this.context.market.useClassicUrls,
      useHashForProductTabs: this.context.market.useHashForProductTabs,
      routeConfig,
      localizedRoutes: routes,
      location,
      marketCode: this.context.market.code,
      experiments: this.props.fetchContext.experiments,
    });

    if (!nextRoute) {
      this.resetTimers();

      this.makeTransition({
        fetchState: DONE,
        data: {},
        errors: null,
      });
      return;
    }

    let useCache = this.cache[nextRoute.key];

    // Abort if route has not changed
    if (
      this.state.route &&
      nextRoute &&
      nextRoute.component &&
      nextRoute.component.reducer
    ) {
      const state = this.routeState[nextRoute.key];

      const newState = nextRoute.component.reducer(state, {
        type: 'URL_CHANGE',
        location,
      });

      // Do not use response cache if state has changed
      // Also reset load orders to avoid any unexpected
      // data merge behaviour
      if (newState !== state) {
        useCache = false;
        this.state.loadOrders[nextRoute.key] = -1;
      }

      // Abort it state did not change and navigation is local
      // within same page
      if (this.state.route.key === nextRoute.key && state === newState) {
        this.resetTimers();
        return;
      }

      this.routeState[nextRoute.key] = newState;
    } else if (this.state.route && this.state.route.key === nextRoute.key) {
      this.resetTimers();
      return;
    }

    const nextRouteId = Math.random()
      .toString(36)
      .substring(7);

    // Preload next chunk for smoother navigation experience
    if (nextRoute && nextRoute.component && nextRoute.component.preload) {
      nextRoute.component.preload();
    }

    if (!nextRoute || !nextRoute.component.graphQL) {
      this.resetTimers();

      this.makeTransition({
        fetchState: DONE,
        data: {},
        errors: null,
        route: nextRoute,
        routeId: nextRouteId,
      });
      return;
    }

    const { key } = nextRoute;

    const groupKey = Routes.getRouteGroupKey(nextRoute);

    this.startTransition(
      {
        nextRouteId,
      },
      nextRoute
    );

    const response = useCache
      ? this.cache[key]
      : await this.getData(nextRoute, nextRouteId, undefined, useCache);

    // Route has changed or getData failed, abort setState
    if (
      (!useCache && this.state.nextRouteId !== nextRouteId) ||
      (!response && this.state.routingError)
    ) {
      return;
    }

    this.resetTimers();

    const isSameRouteGroup =
      groupKey === Routes.getRouteGroupKey(this.state.route);

    this.makeTransition({
      fetchState:
        Routes.getLoadOrder(
          nextRoute.component,
          nextRoute.variables,
          isSameRouteGroup ? this.state.loadedSections[groupKey] : undefined,
          false
        ).length <= 1
          ? DONE
          : PRIMARY_REQUEST_DONE,
      route: nextRoute,
      data: response ? response.data : null,
      errors: response && response.errors ? response.errors : null,
      routeId: nextRouteId,
      dataCacheAge: useCache ? Date.now() - this.dataCacheAge[key] : undefined,
    });

    // Refetch all data when rendering from cache in order to update
    // any changes
    if (useCache) {
      nextIdleTick(
        async () => {
          const refetchedData = await this.getData(
            nextRoute,
            nextRouteId,
            undefined,
            useCache
          );

          if (refetchedData && this.state.routeId === nextRouteId) {
            this.setState(refetchedData);
          }
        },
        { timeout: 5000 }
      );
    }
  }

  async fetchMoreData(fetchMoreVariables) {
    const { route, routeId } = this.state;

    const routeData = await this.getData(route, routeId, fetchMoreVariables);
    // route changed, abort
    if (this.state.routeId !== routeId || !routeData) {
      return;
    }
    const { data, errors } = routeData;
    this.setState({ data, errors });
  }

  static getDerivedStateFromProps(props, state) {
    const { fetchState, route, loadOrders, errors } = state;

    const isInTransition = fetchState < PRIMARY_REQUEST_DONE;

    if (isInTransition) {
      return null;
    }

    if (route && loadOrders[route.key] > 0) {
      return null;
    }

    if (route && !errors) {
      return {
        criticalErrorRoute: route.key,
        criticalError: null,
      };
    }

    if (route && route.showError === false) {
      return {
        criticalErrorRoute: route.key,
        criticalError: null,
      };
    }

    return {
      criticalErrorRoute: route && route.key,
      criticalError: errors && errors.find(e => e.status >= 400),
    };
  }

  render() {
    const {
      location,
      history,
      staticContext,
      isAuthenticated,
      routeConfig,
      loaderAfter,
      personalizationClientId,
    } = this.props;

    const { market } = this.context;
    const {
      data,
      route,
      dataCacheAge,
      showDelayedLoader,
      fetchState,
      showSpinner,
      criticalError,
      criticalErrorRoute,
    } = this.state;
    const { activeCampaign } = this;

    if (criticalError && criticalErrorRoute === route.key) {
      // Set status code serverside so correct code can be returned
      if (this.props.staticContext) {
        this.props.staticContext.status = criticalError.status || 500;
      }

      return (
        <ErrorPage
          metaTags={errorPageMetaTags(
            { status: criticalError.status || 500 },
            this.context
          )}
          routeId="error500"
          locationKey={location.key}
          {...criticalError}
        />
      );
    }

    if (
      showSpinner &&
      fetchState < PRIMARY_REQUEST_DONE &&
      (loaderAfter === 0 || showDelayedLoader === true)
    ) {
      return (
        <ScrollToTop key={location.key}>
          <Loader fadeIn={!showDelayedLoader} />
        </ScrollToTop>
      );
    }

    if (this.state.timeout) {
      const { routingError } = this.state;
      return (
        <ErrorPage
          status={503}
          message={`TransitionRouter, timeout state: ${routingError &&
            routingError.message}`}
          stack={routingError && routingError.stack}
          metaTags={errorPageMetaTags({ status: 503 }, this.context)}
          onRetry={() => {
            this.setState({
              timeout: false,
              fetchState: START,
            });

            this.run();
          }}
          routeId="error503"
          locationKey={location.key}
        />
      );
    }

    if (route) {
      if (!this.state.data && route.component.graphQL) {
        return (
          <ScrollToTop key={location.key}>
            <Loader fadeIn={false} />
          </ScrollToTop>
        );
      }

      return (
        <>
          <LoadIndicator fetching progress={fetchState} />
          <RouteContext.Provider value={this.routeContextValue}>
            <RouteRenderer
              name={route.name}
              location={location}
              market={market}
              data={data}
              state={this.routeState[route.key]}
              dataCacheAge={dataCacheAge}
              activeCampaign={activeCampaign}
              fetchMoreData={(...args) => this.fetchMoreData(...args)}
              history={history}
              match={route.match}
              staticContext={staticContext}
              isAuthenticated={isAuthenticated}
              routeConfig={routeConfig}
              personalizationClientId={personalizationClientId}
            />
          </RouteContext.Provider>
          <ErrorBoundary onError={() => null}>
            <MetaTags
              view={route.name}
              routeId={this.state.routeId}
              locationKey={this.props.location.key}
              metaTags={
                this.state.route.metaTags
                  ? this.state.route.metaTags(
                      {
                        data,
                        hrefLang: this.props.hrefLang,
                        routeName: route.name,
                        ...route.variables,
                      },
                      this.context
                    )
                  : {}
              }
            />
          </ErrorBoundary>
        </>
      );
    }

    // Currently fallback to switch in order to work with the refactor without
    // breaking everything. Will be replaced with a 404 component when finished
    return (
      <Route
        render={router => {
          if (router.staticContext) {
            // eslint-disable-next-line no-param-reassign
            router.staticContext.status = 404;
          }

          return (
            <ErrorPage
              status={404}
              metaTags={errorPageMetaTags({ status: 404 }, this.context)}
              routeId="error404"
              locationKey={location.key}
            />
          );
        }}
      />
    );
  }
}

const noop = () => {};

const { object, string, bool, func, number } = PropTypes;
Routes.propTypes = {
  fetchContext: object.isRequired,
  routes: object.isRequired,
  campaigns: object,
  location: object.isRequired,
  history: object.isRequired,
  personalizationClientId: string,
  pulseEnvironmentId: string,
  staticContext: object,
  isAuthenticated: bool,
  onTransitionStart: func,
  onTransitionEnd: func,
  loaderAfter: number,
  timeoutAfter: number,
  dispatch: func.isRequired,
};

Routes.defaultProps = {
  staticContext: undefined,
  personalizationClientId: '',
  pulseEnvironmentId: '',
  campaigns: undefined,
  isAuthenticated: false,
  onTransitionStart: noop,
  onTransitionEnd: noop,
  loaderAfter: undefined,
  timeoutAfter: 30000,
};

Routes.contextType = AppContext;

const TransitionRouter = withRouter(connect(state => state.globals)(Routes));

export default TransitionRouter;
